// The advisor line (player-reach.md §4, R1305).
//
// The server walks the plan half of the autopilot's goal ladder for this
// empire and sends the advice on the Empire frame: a kind, a catalog row,
// a level or a count, and a reason. An idle Dredger outranks the queue
// with the fleet line (R1567). This module turns that into the one line
// above the queue strip, and into the row a press opens. It is pure: the
// names come in through a lookup the overlay builds from the hello catalog
// and the own fleets, so the module names no building, hull or node
// itself, and the sentences come from `gen/strings.ts`.
//
// The advisor never gives an order. A press opens the panel on the row the
// advice names, or selects the fleet it names, and the player gives the
// order there.
//
// ONE line speaks to a new commander (R2131): the advisor line carries the
// count of the sea trials beside its sentence, and while the advisor has no
// advice its sentence is the next act of the sea trials. The line wraps to
// two rows, and the fold caret opens a longer sentence (R1423).
//
// The switch is the server's (R1566): the world keeps it on the empire and
// states it as `Empire.advisorOn`, and the account keeps it for the next
// season. The client reads both and keeps no copy of its own.

import { AdviceKind, AdviceReason, type Advice } from '../gen/proto/aquaphobia';
import { advisor as S } from '../gen/strings';
import { arrowFace } from './arrowpress';
import { esc } from './esc';
import type { Tab } from './overlay/tables';

/** The EmpireSetting key of the advisor switch. The hub keeps the switch
 *  on the empire (R1566), so the client sends it at a press and never
 *  after a hello. */
export const ADVISOR_KEY = 'advisor';

/**
 * The word an old settings blob stored for the advisor (R1305), or null.
 * The switch lived in the blob until R1566; the client copies an `on` or
 * an `off` it finds to the account once, while the account still says
 * `auto` (`ui/settings.ts` `copyLegacyAdvisor`).
 */
export function legacyAdvisorWord(blob: unknown): 'on' | 'off' | null {
  if (typeof blob !== 'object' || blob === null) return null;
  const word = (blob as { advisor?: unknown }).advisor;
  return word === 'on' || word === 'off' ? word : null;
}

/** The catalog name of one row the advice names, or undefined. */
export type AdviceNames = (kind: AdviceKind, id: number) => string | undefined;

/**
 * The advisor line, without the ADVISOR tag: what to do, then why. An
 * advice whose row the catalog does not name gives '' and no line.
 */
export function adviceLine(a: Advice, names: AdviceNames): string {
  const name = names(a.kind, a.id);
  if (name === undefined) return '';
  const saving = a.reason === AdviceReason.ADVICE_REASON_SAVING;
  let what: string;
  switch (a.kind) {
    case AdviceKind.ADVICE_KIND_BUILDING:
      what = saving ? S.save_building({ name, level: a.amount })
        : a.amount <= 1 ? S.building_first({ name })
          : S.building_raise({ name, level: a.amount });
      break;
    case AdviceKind.ADVICE_KIND_SHIP:
      what = saving ? S.save_ship({ name }) : S.ship({ name, count: a.amount });
      break;
    case AdviceKind.ADVICE_KIND_RESEARCH: what = S.research({ name }); break;
    case AdviceKind.ADVICE_KIND_TORPEDO: what = S.torpedo({ name, count: a.amount }); break;
    case AdviceKind.ADVICE_KIND_PRINT: what = S.print({ name }); break;
    case AdviceKind.ADVICE_KIND_DESIGN: what = S.design({ name }); break;
    // The fleet line names the fleet and no target (R1307, R1567).
    case AdviceKind.ADVICE_KIND_FLEET: return S.fleet_idle({ name });
    // The commission line names the kind of the open offer (v2 §10,
    // R2634, R2645). The hub sends it only while `commissions` is latched
    // and the advisor is on, which is a new commander's default (R1566).
    case AdviceKind.ADVICE_KIND_COMMISSION: return S.commission({ name });
    default: return '';
  }
  return saving ? `${what} ${S.saving({ pct: Math.floor(a.heldPermille / 10) })}` : what;
}

/** Where a press on the line goes: the menu, its pill, and a CSS selector
 *  of the control that marks the row inside the pill's body. */
export interface AdviceTarget {
  tab: Tab;
  sub: string;
  selector: string;
}

/**
 * The row an advice names. The four BUILD pills mark a row by its info
 * button (`data-kind`, `data-id`); a print is the PRINT button of its
 * master, and a draft is the hull pick of the INVENTION form. A commission
 * is the OPEN press of its row of FLEET · COMMISSIONS. The fleet
 * line names no row: a press selects the fleet (`adviceFleet`).
 */
export function adviceTarget(a: Advice): AdviceTarget | null {
  const info = (kind: string) => `[data-act="info"][data-kind="${kind}"][data-id="${a.id}"]`;
  switch (a.kind) {
    case AdviceKind.ADVICE_KIND_BUILDING: return { tab: 'BUILD', sub: 'BASE', selector: info('building') };
    case AdviceKind.ADVICE_KIND_RESEARCH: return { tab: 'BUILD', sub: 'TECH', selector: info('tech') };
    case AdviceKind.ADVICE_KIND_SHIP: return { tab: 'BUILD', sub: 'SHIPYARD', selector: info('ship') };
    case AdviceKind.ADVICE_KIND_TORPEDO: return { tab: 'BUILD', sub: 'TORPEDO', selector: info('torpedo') };
    case AdviceKind.ADVICE_KIND_PRINT:
      return { tab: 'DRAFT', sub: 'MASTERS', selector: `[data-act="print"][data-id="${a.id}"]` };
    case AdviceKind.ADVICE_KIND_DESIGN:
      return { tab: 'DRAFT', sub: 'INVENTION', selector: `[data-act="invhull"][data-id="${a.id}"]` };
    // The commission line opens FLEET · COMMISSIONS on the row of the
    // offer, whose OPEN opens the card with the fleets that fit (R2645).
    case AdviceKind.ADVICE_KIND_COMMISSION:
      return { tab: 'FLEET', sub: 'COMMISSIONS', selector: `${info('commission')}.bp` };
  }
  return null;
}

/** The own fleet the fleet line names, or null for every other advice
 *  (R1567). A press selects it and opens its window, and gives no order. */
export function adviceFleet(a: Advice): number | null {
  return a.kind === AdviceKind.ADVICE_KIND_FLEET ? a.id : null;
}

/** The ADVISOR tag, for the overlay. */
export const ADVISOR_LABEL = S.label;
/** The hover of the line. */
export const ADVISOR_TITLE = S.title;

/** One frame of the advisor line, as the overlay hands it over (R2131). */
export interface AdviceRowView {
  /** The sentence: the advice, or the next act of the sea trials. */
  line: string;
  /** The press of the sentence: the row of the advice, or the panel of
   *  the next act. Neither gives an order (R1305, R1307). */
  act: 'advice' | 'trialnext';
  /** The hover of the sentence. */
  title: string;
  /** The ADVISOR tag stands: the advisor is on. */
  tag: boolean;
  /** The welcome card marks the line with its tag 1 (R1572): trusted
   *  markup, or ''. */
  callout: string;
  /** The quiet count of the sea trials (`trialsCountHtml`): trusted
   *  markup, or ''. */
  count: string;
  /** The line stands open to the whole sentence (R1423). */
  open: boolean;
}

/**
 * The advisor line, or '' with no sentence. The sentence wraps to two
 * rows inside `.al`; the fold caret stands while a longer sentence is cut,
 * and the overlay hides it on a line that fits (`fits`, R1775).
 */
export function adviceRowHtml(v: AdviceRowView): string {
  if (v.line === '') return '';
  return `<div class="advrow${v.open ? ' open' : ''}${v.callout ? ' callout' : ''}">`
    + `<button class="advice" data-act="${v.act}" title="${esc(v.title)}"><span class="al">`
    + `${v.callout}${v.tag ? `<span class="atag">${S.label}</span> ` : ''}${esc(v.line)}</span></button>`
    + v.count
    + `<button class="afold" data-act="advicefold" title="${esc(v.open ? S.fold : S.unfold)}">`
    + `${arrowFace(v.open ? 'down' : 'right')}</button></div>`;
}
